import {
  Heading,
  OptionsSelectField,
  Stack,
  SubmissionForm,
  SubmissionFormButtons,
  TextBadge,
  TextBadgeColor,
  TextInputField,
  useTheme
} from "@bps/fluent-ui";
import { Validator } from "@bps/validator";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  AuthActionType,
  FieldDeviceAuthAction
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  useAuthActionsRefData,
  useFieldDeviceIdentifiers
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

export interface AuthActionFormValues {
  deviceAction: string | undefined;
  deviceIdentifierType: string | undefined;
  deviceIdentifierValue: string | undefined;
}

interface AuthActionFormProps {
  fieldDeviceId: string;
  authAction?: FieldDeviceAuthAction;
  onSubmit: (values: AuthActionFormValues) => Promise<void>;
}

const sendNotificationValidator = new Validator<AuthActionFormValues>(
  validator => ({
    deviceIdentifierType: validator
      .string()
      .required({ message: "Identifier type is required" }),

    deviceIdentifierValue: validator
      .string()
      .maxLength(255)
      .required(),
    deviceAction: validator
      .string()
      .required({ message: "Device action is required" })
  })
);

export const AuthActionForm: React.FC<AuthActionFormProps> = ({
  onSubmit,
  fieldDeviceId,
  authAction
}) => {
  const theme = useTheme();
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const { data: authActionRefData } = useAuthActionsRefData();
  const { data: identifiers } = useFieldDeviceIdentifiers(fieldDeviceId);

  const idenitiferOptions =
    identifiers
      ?.map(identifier => identifier.identifierType)
      ?.filter((value, index, array) => array.indexOf(value) === index)
      .map(identifier => {
        return {
          key: identifier,
          text: identifier
        };
      }) ?? [];

  const actionOptions =
    authActionRefData?.map(refData => {
      return { key: refData.code, text: refData.code };
    }) ?? [];

  return (
    <SubmissionForm<AuthActionFormValues>
      validate={sendNotificationValidator.validate}
      initialValues={{
        deviceAction: authAction?.deviceAction,
        deviceIdentifierType: authAction?.deviceIdentifierType,
        deviceIdentifierValue: authAction?.deviceIdentifierValue
      }}
      disabled={!hasWritePermission}
      onSubmit={onSubmit}
      hideButtons
    >
      {({ values }) => {
        const currentAction = authActionRefData?.find(
          option => option.code === values.deviceAction
        );

        const isActionSuccess = currentAction?.code == AuthActionType.Succeed;

        return (
          <Stack tokens={{ childrenGap: theme.spacing.m }}>
            <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
              <Heading>On next authentication failure, if:</Heading>

              <OptionsSelectField
                label="Identifer Type is"
                required
                name="deviceIdentifierType"
                options={idenitiferOptions}
              />

              <TextInputField
                label="Identifer Value is"
                required
                name="deviceIdentifierValue"
              />

              <OptionsSelectField
                required
                styles={{ root: { width: "60%" } }}
                name="deviceAction"
                label="Action"
                options={actionOptions}
              />

              {currentAction && (
                <TextBadge
                  badgeColor={
                    isActionSuccess
                      ? TextBadgeColor.green
                      : TextBadgeColor.yellow
                  }
                >
                  {currentAction?.text}
                </TextBadge>
              )}
            </Stack>
            <SubmissionFormButtons hideCancelButton />
          </Stack>
        );
      }}
    </SubmissionForm>
  );
};
